<template>
  <div v-if="!props.hideFooter" class="footer">
    <div :class="{ 'flex-center-md': !isEstablishedAccount, 'opposite-ends-desktop': isEstablishedAccount }" class="mb">
      <div :class="{ 'mr-md': !isEstablishedAccount }" class="mb-mobile">
        <span class="mr-half block-mobile" @click="handleCarboniteLabelClick">{{ t("Brand.carbonite") }}:</span>
        <ul class="pipe">
          <li>
            <a id="LinkFooterPrivacyPolicy" :href="privacyUrl" target="_blank" :title="tn('privacyPolicy')">
              {{ tn("privacyPolicy") }}
            </a>
          </li>
          <li>
            <a id="LinkFooterTOS" :href="termsUrl" target="_blank" :title="tn('termsOfService')">
              {{ tn("termsOfService") }}
            </a>
          </li>
          <li>
            <a id="LinkFooterContactUs" :href="getUrl('VITE_APP_CONTACT_US')" target="_blank" :title="tn('contactUs')">
              {{ tn("contactUs") }}
            </a>
          </li>
          <li>
            <a id="LinkFooterAgent" href="https://help.carbonite.com/" target="_blank" :title="tn('agent')">
              {{ tn("agent") }}
            </a>
          </li>
          <li>
            <a href="#" class="ot-sdk-show-settings">
              {{ tn("cookieSettings") }}
            </a>
          </li>
        </ul>
      </div>
      <div v-if="showWebrootLinks" class="mb-mobile">
        <!-- intentionally throw an unhandled exception outside a Vue component -->
        <span
          onclick="setTimeout(() => { throw new Error('Non-Vue Easter Egg') }, 100)"
          class="mr-half block-mobile"
          @click="handleWebrootLabelClick"
        >
          {{ t("Brand.webroot") }}:
        </span>
        <ul class="pipe">
          <li>
            <a
              id="LinkFooterWebrootPrivacy"
              :href="`https://www.webroot.com/${languageUrlParm}/legal/privacy`"
              target="_blank"
              :title="tn('privacyPolicy')"
            >
              {{ tn("privacyPolicy") }}
            </a>
          </li>
          <li>
            <a
              id="LinkFooterWebrootTerms"
              :href="`https://www.webroot.com/${languageUrlParm}/legal/service-terms-and-conditions`"
              target="_blank"
              :title="tn('termsOfService')"
            >
              {{ tn("termsOfService") }}
            </a>
          </li>
          <li>
            <a
              id="LinkFooterWebrootSupport"
              :href="`https://www.webroot.com/${languageUrlParm}/support/support-home`"
              target="_blank"
              :title="tn('support')"
            >
              {{ tn("support") }}
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div id="copyright" :class="{ 'flex-center-md': !isEstablishedAccount }">
      <i18n-t keypath="FooterComponent.copyright" tag="p" scope="global">
        <template #currentYear>&copy;{{ buildDate }}</template>
        <template #copyrightBrand>{{ t("Brand.opentext") }}. {{ tn("allRightsReserved") }}.</template>
      </i18n-t>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { logEvent } from "@/common/logger";
import { t } from "@/i18n";
import { BUILD_DATE } from "@/define";
import { useConfigStore } from "@/stores/config";
import { useUserStore } from "@/stores/user";
import { getLanguageUrlParm } from "@/common/helper";
import { getUrl } from "@/common/getUrl";
import { retailCodes } from "@/common/featureDisplayAccess";
import { useRoute } from "vue-router";

const name = "FooterComponent";

const props = defineProps({
  hideFooter: {
    type: Boolean,
  },
});

logEvent("created", name);
const userStore = useUserStore();
const configStore = useConfigStore();
const route = useRoute();
const termsUrl = ref("");
const privacyUrl = ref("");
const buildDate = computed(() => new Date(BUILD_DATE).getFullYear());
const languageUrlParm = computed(() => getLanguageUrlParm(userStore.selectedLanguage || "en"));
const isOnAolAccountCreationRoute = computed(() => {
  return route.matched.some(r => r.path.toLowerCase().includes("/aol"));
});
const isEstablishedAccount = userStore.isEstablishedAccount;

const user = computed(() => {
  return userStore.currentUser;
});

const showWebrootLinks = computed(() => {
  if (window.location.hostname.toLowerCase().indexOf("webroot") > -1) {
    return true;
  }

  const isAol =
    (user.value && user.value.subscriberInfo?.carbLicenseDistributionMethodCodes.some(l => l === retailCodes.AOL)) ||
    isOnAolAccountCreationRoute.value;

  return !isAol;
});

function tn(v: string, params?: Record<string, unknown>): string {
  return t(`${name}.${v}`, params);
}

onMounted(async () => {
  logEvent("mounted", name);
  await configStore.getConfig();
  const links = configStore.config?.footerLinks;
  termsUrl.value = links?.terms ?? "";
  privacyUrl.value = links?.privacy ?? "";
});

function handleCarboniteLabelClick() {
  logEvent("Carbonite label click", "FooterComponent");
  //intentionally throw an unhandled exception inside a Vue component
  throw new Error("Vue Easter Egg");
}

function handleWebrootLabelClick() {
  logEvent("Webroot label click", "FooterComponent");
}
</script>
